import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  FiArrowLeft,
  FiEdit2,
  FiPaperclip,
  FiDownload,
  FiFile,
  FiX,
  FiMaximize2,
} from 'react-icons/fi';
import {
  useGetBroadcastEmail,
  useGetBroadcastAttachmentContent,
  useGetMailingList,
} from '../api/mailing-lists';

interface BroadcastSearch {
  listUuid?: string;
}

export const Route = createFileRoute('/mail/broadcasts/$broadcastUuid')({
  validateSearch: (search: Record<string, unknown>): BroadcastSearch => ({
    listUuid: typeof search.listUuid === 'string' ? search.listUuid : undefined,
  }),
  component: BroadcastViewPage,
});

const IMAGE_EXTENSIONS: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
};

function getMimeFromName(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS[ext] || 'application/octet-stream';
}

function BroadcastViewPage() {
  const { broadcastUuid } = Route.useParams();
  const { listUuid } = Route.useSearch();

  // Fetch specific broadcast detail
  const {
    data: detailData,
    isLoading,
    isPlaceholderData,
  } = useGetBroadcastEmail(listUuid, broadcastUuid);
  const broadcast = detailData?.data;

  const attachmentName = broadcast?.attachments?.[0]?.name;
  const hasAttachment = !!attachmentName && !isPlaceholderData;

  const { data: attachmentContentData, isLoading: isLoadingAttachment } =
    useGetBroadcastAttachmentContent(listUuid, broadcastUuid, hasAttachment);

  const attachmentBase64 = attachmentContentData?.content;
  const attachmentMime = attachmentName ? getMimeFromName(attachmentName) : '';
  const isImage = attachmentMime.startsWith('image/');
  const isPdf =
    attachmentMime === 'application/pdf' || attachmentName?.toLowerCase().endsWith('.pdf');
  const isText =
    ['text/plain', 'text/csv', 'text/html'].includes(attachmentMime) ||
    attachmentName?.match(/\.(txt|csv|html?|json|xml|js|ts|jsx|tsx|css|scss|py|sh|md)$/i);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch list details to get list name
  const { data: listDetails } = useGetMailingList(listUuid);
  const mailingListObj = listDetails?.data?.mailing_list;

  if (isLoading && !broadcast) {
    return (
      <div className="flex-1 flex items-center justify-center rounded-[16px] border border-[#262626] bg-[#0E0E0E]">
        <p className="text-sm text-[#737373]">Loading...</p>
      </div>
    );
  }

  if (!broadcast) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center rounded-[16px] border border-[#262626] bg-[#0E0E0E] text-center px-8">
        <p className="text-sm text-[#737373] mb-3">Broadcast not found</p>
        <Link to="/mail" className="text-sm text-[#2F80ED] hover:text-[#4A9AF0] transition-colors">
          Go back to Mail
        </Link>
      </div>
    );
  }

  const isDraft = broadcast.status === 'draft';
  const statusLabel =
    broadcast.status === 'sent'
      ? 'Sent'
      : broadcast.status === 'draft'
        ? 'Draft'
        : broadcast.status === 'scheduled'
          ? 'Scheduled'
          : 'Sending';

  const toLabel = mailingListObj?.name || 'Loading...';

  return (
    <div className="flex-1 flex flex-col rounded-none md:rounded-[16px] border-0 md:border border-[#262626] bg-[#0E0E0E] overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-8 py-4 md:py-6 border-b border-[#262626]">
        {/* Mobile-only back button if needed, but in standard desktop we hide it or keep it small */}
        <div className="md:hidden flex items-center gap-4 mb-3">
          <Link
            to="/mail"
            className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
          >
            <FiArrowLeft size={18} />
            Back
          </Link>
        </div>

        <div className="flex flex-col">
          {/* Status Label (e.g. "Sent", "Draft") */}
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">{statusLabel}</h2>
            {(isDraft || broadcast.status === 'scheduled') && (
              <Link
                to="/mail/compose"
                search={{ draftUuid: broadcast.uuid, listUuid: listUuid }}
                className="flex items-center gap-1 md:gap-1.5 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white/10 text-white text-xs md:text-sm font-medium hover:bg-white/20 transition-colors flex-shrink-0"
              >
                <FiEdit2 size={12} className="md:hidden" />
                <FiEdit2 size={14} className="hidden md:block" />
                <span className="hidden sm:inline">Continue Editing</span>
                <span className="sm:hidden">Edit</span>
              </Link>
            )}
          </div>

          {/* To Field */}
          <p className="text-sm text-[#737373] mb-1">
            To: <span className="text-[#A3A3A3] font-medium">{toLabel}</span>
          </p>

          {/* Subject Field */}
          <p className="text-sm text-[#737373]">
            Subject: <span className="text-[#C0C0C0] font-medium">{broadcast.subject}</span>
          </p>

          {/* Details / Time */}
          <div className="flex items-center gap-2 mt-2">
            {broadcast.sent_at && (
              <span className="text-xs text-[#525252]">
                {new Date(broadcast.sent_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            )}
            {broadcast.scheduled_at && !broadcast.sent_at && (
              <span className="text-xs text-[#525252]">
                Scheduled for:{' '}
                {new Date(broadcast.scheduled_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-4 md:px-8 py-4 md:py-6 overflow-y-auto">
        <div className="prose prose-invert max-w-none">
          <div
            className="text-white leading-relaxed text-sm whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: broadcast.body || 'No content' }}
          />
        </div>

        {/* Attachments */}
        {hasAttachment && attachmentName && (
          <div className="mt-6 pt-6 border-t border-[#262626]">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FiPaperclip size={13} className="text-[#525252]" />
                <span className="text-xs font-medium text-[#525252] uppercase tracking-wider">
                  Attachment
                </span>
              </div>
              {attachmentBase64 && (
                <a
                  href={`data:${attachmentMime};base64,${attachmentBase64}`}
                  download={attachmentName}
                  className="flex items-center gap-1.5 text-xs text-[#2F80ED] hover:text-[#4A9AF0] transition-colors"
                  title="Download"
                >
                  <FiDownload size={14} />
                </a>
              )}
            </div>

            {/* Gmail-style attachment card */}
            <div className="flex flex-wrap gap-4">
              {/* Attachment card */}
              <button
                onClick={() => attachmentBase64 && isImage && setPreviewOpen(true)}
                className={`group relative w-full sm:w-[220px] bg-white/[0.02] border border-[#262626] rounded-lg overflow-hidden transition-all hover:border-[#3a3a3a] ${
                  attachmentBase64 && isImage ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                {/* Preview area */}
                <div className="h-[140px] bg-white/[0.03] flex items-center justify-center relative overflow-hidden">
                  {/* Download button on hover */}
                  {attachmentBase64 && (
                    <a
                      href={`data:${attachmentMime};base64,${attachmentBase64}`}
                      download={attachmentName}
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-black/80"
                      onClick={(e) => e.stopPropagation()}
                      title="Download"
                    >
                      <FiDownload size={13} className="text-white" />
                    </a>
                  )}
                  {isLoadingAttachment ? (
                    <div className="flex flex-col items-center gap-2">
                      <span className="inline-block w-5 h-5 border-2 border-[#525252] border-t-transparent rounded-full animate-spin" />
                      <span className="text-[10px] text-[#525252]">Loading...</span>
                    </div>
                  ) : attachmentBase64 && isImage ? (
                    <img
                      src={`data:${attachmentMime};base64,${attachmentBase64}`}
                      alt={attachmentName}
                      className="w-full h-full object-cover"
                    />
                  ) : attachmentBase64 && isPdf ? (
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <FiFile size={24} className="text-red-400" />
                      </div>
                      <span className="text-[10px] text-[#525252]">PDF</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-12 h-12 rounded-lg bg-[#2F80ED]/10 flex items-center justify-center">
                        <FiFile size={24} className="text-[#2F80ED]" />
                      </div>
                      <span className="text-[10px] text-[#525252]">
                        {attachmentName.split('.').pop()?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  {/* Hover overlay for images */}
                  {attachmentBase64 && isImage && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <FiMaximize2 size={20} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Footer bar with filename + fold */}
                <div className="flex items-center gap-2.5 px-3 py-2.5 bg-white/[0.03] border-t border-[#262626] relative">
                  {/* File type icon */}
                  <div
                    className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${
                      isPdf ? 'bg-red-500' : isText ? 'bg-[#2F80ED]' : 'bg-[#525252]'
                    }`}
                  >
                    {isPdf ? 'P' : isText ? 'T' : 'F'}
                  </div>
                  <span
                    className="text-xs text-[#A3A3A3] truncate flex-1 text-left"
                    title={attachmentName}
                  >
                    {attachmentName}
                  </span>
                  {/* Folded corner */}
                  <div
                    className="absolute bottom-0 right-0 w-5 h-5"
                    style={{
                      background:
                        'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)',
                    }}
                  />
                  <div
                    className="absolute bottom-0 right-0 w-5 h-5 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(135deg, transparent 50%, #1a1a1a 50%, rgba(255,255,255,0.15) 52%, rgba(255,255,255,0.05) 100%)',
                      clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                    }}
                  />
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Attachment preview modal */}
      {previewOpen && attachmentBase64 && isImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewOpen(false)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            onClick={() => setPreviewOpen(false)}
          >
            <FiX size={20} className="text-white" />
          </button>
          <img
            src={`data:${attachmentMime};base64,${attachmentBase64}`}
            alt={attachmentName}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Analytics */}
      {!isDraft && (
        <div className="px-4 md:px-8 py-3 md:py-3.5 border-t border-[#262626] bg-[#0A0A0A]/20">
          <div className="flex items-center gap-3 md:gap-4 text-[10px] md:text-xs text-[#525252]">
            <span>
              <strong className="text-white font-semibold">{broadcast.recipient_count}</strong>{' '}
              recipients
            </span>
            <span>·</span>
            <span>
              <strong className="text-[#00D26A] font-semibold">{broadcast.succeeded_count}</strong>{' '}
              delivered
            </span>
            <span>·</span>
            <span className={broadcast.failed_count > 0 ? 'text-red-400/80' : ''}>
              <strong
                className={`${broadcast.failed_count > 0 ? 'text-red-400' : 'text-[#525252]'} font-semibold`}
              >
                {broadcast.failed_count}
              </strong>{' '}
              failed
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
